import { Dropdown } from '@platform/ui-library';
import { useState } from 'react';
import { Option } from '../types';

type Label = JSX.Element | string | number;

/**
 * Allowed values for options created from items.
 *
 * @note This is only needed until the @see Dropdown is updated to support arrays of options.
 */
type Value = string;

/** Simple item that @see ItemDropdown can get label & value from by itself. */
type BasicItem<TValue> = (
  Option<TValue>
  | {
    id: TValue;
    name?: Label | null;
  }
);

type OptionGetters<TItem, TValue> = {
  /** Gets the value for an item. */
  getValue: (item: TItem) => TValue;
  /** Gets the label for an item. */
  getLabel: (item: TItem) => Label;
};

const emptyOptionValue = '<--none-->';

/** Value properties that need to change based on the value of @see required. */
export type ItemDropdownValueProps<TItem, TValue> = (
  {
    /** Emits when user selects an option. */
    onChange: (newValue: TValue | null, newItem: TItem | null) => void;
    /** A value is not required in the dropdown. */
    required?: boolean;
    /** Currently selected value. */
    value: TValue | null | undefined;
  } | {
    /** Emits when user selects an option. */
    onChange: (newValue: TValue, newItem: TItem) => void;
    /** A value may be required in the dropdown. */
    required: true;
    /** Currently selected value. */
    value: TValue;
  }
);

export type ItemDropdownProps<TItem, TValue> = (
  ItemDropdownValueProps<TItem, TValue>
  & (
    // Label & value getters are only required for non-basic items.
    TItem extends BasicItem<TValue> ? (
      Partial<OptionGetters<TItem, TValue>>
    ) : (
      OptionGetters<TItem, TValue>
    )
  ) & {
    /** Items to create options from. */
    items: TItem[] | null | undefined;
    /** Labels used on the UI components. */
    labels?: {
      /** Overrides whatever would normally be shown in the dropdown. */
      displayText?: string;
      /** If {null} is sent, then the empty option will be removed. */
      emptyOptionLabel?: string | null;
      /** Displayed when @see value is not found in @see items. */
      invalidOption?: string;
      /** Displayed @see items is empty. */
      noOptions?: string;
      /** Displayed when there are items, but none are selected. */
      placeholder?: string;
    };
    /**
     * Whether state should be used instead of props for value.
     *
     * @deprecated This should only be used by node models because they are not React classes with state.
     */
    useStateValue?: boolean;
  }
);

/** Used for dropdowns that use @see Dropdown, but provide items for the consumer. */
export type ItemDropdownPropsWithoutItems<TItem, TValue> = (
  // Specify value properties specifically since @see Omit mixes required & not required posibilities together.
  ItemDropdownValueProps<TItem, TValue>
  & (
    Omit<ItemDropdownProps<TItem, TValue>, (
      'items'
      | 'getLabel'
      | 'getValue'
    )>
  )
);

/** Dropdown for an array of items. */
export function ItemDropdown<TItem, TValue extends Value>({
  labels = {},
  getLabel = (item) => {
    return (('label' in item) ? item.label : item.name) ?? '<Unnamed>';
  },
  getValue = (item) => {
    return ((('id' in item) ? item.id : item.value) ?? '') as TValue;
  },
  onChange,
  items,
  required,
  useStateValue,
  value
}: ItemDropdownProps<TItem, TValue>): JSX.Element {
  const [stateValue, setStateValue] = useState(value);
  if (useStateValue) {
    value = stateValue;
  }
  const itemMap: Record<string, TItem> = Object.fromEntries(
    items?.map((item): [string, TItem] => {
      return [getValue(item), item];
    }) ?? []
  );
  let options = Object.fromEntries(
    Object.entries(itemMap).map(([
      id,
      item
    ]): [string, Label] => {
      return [id, getLabel(item)];
    }).sort(([_valueA, labelA], [_valueB, labelB]) => {
      return (
        typeof labelA !== 'string'
        || typeof labelB !== 'string'
      ) ? 0 : labelA.localeCompare(labelB);
    })
  );

  if (!required) {
    options = {
      [emptyOptionValue]: labels.emptyOptionLabel ?? '<None>',
      ...options
    };
  }

  const getDisplayText = (): Label => {
    if (labels.displayText) {
      return labels.displayText;
    } else if (!items?.length) {
      return labels.noOptions ?? '<No Options>';
    } else if (!value) {
      return labels.placeholder ?? 'Select an option.';
    }
    const selectedLabel = options[value];
    return (selectedLabel) ? selectedLabel : (
      labels.invalidOption ?? '<Invalid Value>'
    );
  };

  return (
    <Dropdown
      displayText={getDisplayText()}
      onChange={(newId) => {
        const [newValue, newItem] = (newId === emptyOptionValue) ? (
          [null, null]
        ) : (
          [newId as TValue, itemMap[newId]]
        );
        onChange(newValue as TValue, newItem as TItem);
        setStateValue(newValue);
      }}
      options={options}
      value={value ?? undefined}
    />
  );
}
